import * as React from "react";
import "./multiSelectDropDown.scss";
import {isNullOrUndefined} from "util";
import DropDownWrapper from "../DropDownWrapper/DropDownWrapper";
import MultiSelectList from "../MultiSelectList/MultiSelectList";
import TextDropDownWrapper from "../TextDropDownWrapper/TextDropDownWrapper";

interface IProps<T> {
	/**
	 * List of items.
	 */
	items: T[];
	/**
	 * Converts item to item label.
	 * @param {T} item
	 * @return {string}
	 */
	itemToLabel?: (item: T) => string;
	/**
	 * Converts item to item id.
	 * @param {T} item
	 * @return {string}
	 */
	itemToId?: (item: T) => string;
	/**
	 * On delete callback.
	 * @param {string} id
	 */
	onSelect?: (item: T[]) => void;
	/**
	 * Selected item id.
	 */
	selectedItemIds?: string[];
}

interface IState {
	/**
	 * Indicates if dropdown is visible.
	 */
	dropDownVisible: boolean;
	/**
	 * Selected item id.
	 */
	selectedItemIds: (string | null | undefined)[];
}

export default class MultiSelectDropDown<T> extends React.Component<IProps<T>, IState> {
	constructor(props: IProps<T>) {
		super(props);
		this.state = {
			dropDownVisible: false,
			selectedItemIds: props.selectedItemIds
		};
		this.onDropDownToggle = this.onDropDownToggle.bind(this);
		this.onItemsSelect = this.onItemsSelect.bind(this);
		this.getSelectedItem = this.getSelectedItem.bind(this);
		this.selectedItemsToText = this.selectedItemsToText.bind(this);
		this.handleOutsideClick = this.handleOutsideClick.bind(this);
		this.toggleDropdown = this.toggleDropdown.bind(this);
		this.onShowAllItemsClick = this.onShowAllItemsClick.bind(this);
	}

	dropdown: HTMLDivElement;

	componentWillReceiveProps(newProps: IProps<T>) {
		this.setState({selectedItemIds: newProps.selectedItemIds});
	}

	componentDidMount() {
		document.addEventListener("mousedown", this.handleOutsideClick);
	}

	componentWillUnmount() {
		document.removeEventListener('mousedown', this.handleOutsideClick);
	}

	render() {
		return (
			<TextDropDownWrapper text={this.selectedItemsToText()}
								 onClick={this.toggleDropdown}
								 onRemoveClick={() => {}}
								 removable={false}
								 onShowAllItems={this.getShowAllFunction()}>
				{this.state.dropDownVisible &&
				<DropDownWrapper onOutsideClick={() => {this.setState({dropDownVisible: false})}}>
					<MultiSelectList items={this.props.items}
									 itemToId={this.props.itemToId}
									 itemToLabel={this.props.itemToLabel}
									 selectedItemIds={this.props.selectedItemIds}
									 onUpdate={this.onItemsSelect}/>
				</DropDownWrapper>
				}
			</TextDropDownWrapper>
		);
	}

	/**
	 * Returns function handler for show all link click action or null if all items are selected
	 * @returns {() => void}
	 */
	getShowAllFunction() {
		return !this.areAllSelected(this.props.items, this.props.selectedItemIds) ? this.onShowAllItemsClick : null
	}

	/**
	 * Select all items
	 */
	onShowAllItemsClick() {
		this.onItemsSelect(this.props.items);
	}

	/**
	 * Toggle dropdown
	 */
	toggleDropdown() {
		this.setState({dropDownVisible: !this.state.dropDownVisible});
	}


	/**
	 * Map selected item (if any) to displayable value.
	 * @return {string}
	 */
	selectedItemsToText(): string {
		if (this.areAllSelected(this.props.items, this.props.selectedItemIds)) {
			return "Showing all items";
		}
		else {
			return this.props.selectedItemIds.length > 0 ? "Filters are active" : "No categories selected";
		}
	}

	/**
	 * Check if all items are selected (exclude disabled items)
	 * @param {T[]} items - all items
	 * @param {string[]} selectedIds - ids of selected items
	 * @returns {boolean}
	 */
	areAllSelected(items: T[], selectedIds: string[]) {
		return items.filter(item => selectedIds.indexOf(this.props.itemToId(item)) < 0).length == 0;
	}


	/**
	 * Gets selected item from items list.
	 * @param {string} itemId
	 * @return {any}
	 */
	getSelectedItem(itemId: string) {
		return this.props.items.filter((item) => this.props.itemToId(item) === itemId)[0];
	}

	/**
	 * On item selected.
	 * @param {T} item
	 */
	onItemsSelect(items: T[]) {
		this.setState({dropDownVisible: false, selectedItemIds: items.map(this.props.itemToId)});
		if (!isNullOrUndefined(this.props.onSelect))
			this.props.onSelect(items);
	}

	onDropDownToggle() {
		this.setState({dropDownVisible: !this.state.dropDownVisible});
	}

	/**
	 * Handle click outside dropdown list.
	 */
	handleOutsideClick(event: any) {
		if (!event || (this.dropdown && !this.dropdown.contains(event.target as HTMLElement))) {
			this.setState({dropDownVisible: false})
		}
	}
}
